import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { requisitionSlipsApi } from '../requisitionSlipsApi';

/**
 * Withdraws a slip, with the reason.
 *
 * **A modal because the reason is required**, and because withdrawing is the state most easily
 * confused with deleting — the body says which this is before the tick.
 */
export function CancelRequisitionSlipModal({
  slipId,
  slipNumber,
  isSubmitted = false,
  onClose,
  onCancelled,
}: {
  slipId: string;
  slipNumber: string;
  /** Whether an approval is pending on it, so the dialog can say it will be closed. */
  isSubmitted?: boolean;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = reason.trim().length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await requisitionSlipsApi.cancel(slipId, reason.trim());
      onCancelled();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not withdraw this slip.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'cancel-requisition-slip' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Withdraw ${slipNumber}`}
      size="lg"
      id="cancel-requisition-slip-modal"
      /*
        NO Autogenerate. Why an office changed its mind is a judgement somebody is making about
        a specific slip — generating one would put words in their mouth, which is the one place
        the generator would be actively wrong.
      */
      confirm={{
        id: 'cancel-requisition-slip-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="cancel-requisition-slip-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="cancel-requisition-slip-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          WITHDRAWING IS NOT DELETING, and the two are the most confusable states here.
          Withdrawn is an OUTCOME the register keeps; a delete is a correction to the register.
          Stated before the tick, and pointing a reader who reached for the wrong one at the
          other.
        */}
        <div className="flex flex-col gap-2 text-xs text-slate-600">
          <p>
            <strong>Withdrawn is an outcome the register keeps.</strong> The slip stays visible,
            marked withdrawn, with this reason against it — which is what a reader looking back
            at the year wants to find.
          </p>
          <p>
            If it was raised in error, or twice, <strong>Delete</strong> is the one you want
            instead.
          </p>
          {isSubmitted ? (
            <p>Any approval still pending on it is closed in the same action.</p>
          ) : null}
        </div>

        <Textarea
          id="cancel-requisition-slip-reason"
          label="Reason"
          required
          description="Why the office no longer needs these supplies. It stays on the record."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={reason.trim() ? error?.fieldError('reason') : 'Say why it is being withdrawn.'}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
